import * as Sentry from '@sentry/react'

import { extractSchemaError } from './utils/errorHelpers'

// ▶ helper for status→string
function defaultMessage(status) {
  const map = {
    400: 'Bad request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not found',
    500: 'Server error',
  }
  return map[status] ?? `Error ${status}`
}
export default class ApiClient {
  constructor(onError, { enableSocket = false } = {}) {
    this.onError = onError
    this.enableSocket = enableSocket
    this.socket = null
  }

  async request(options) {
    const method = (options?.method || 'GET').toUpperCase()
    const canRetry =
      !options?.__retried && ['GET', 'HEAD', 'OPTIONS'].includes(method)

    let response = await this.requestInternal(options)

    // One quick retry for transient 500s on safe/idempotent methods
    if (!response.ok && response.status === 500 && canRetry) {
      response = await this.requestInternal({ ...options, __retried: true })
    }

    if (!response.ok) {
      // single onError call for all non-OK statuses
      if (typeof this.onError === 'function') {
        const { status, body } = response

        const defaultMsg = defaultMessage(status)
        const { fields, pretty } = extractSchemaError(body)

        const error = {
          code: body?.code ?? status,
          message: body?.message ?? pretty ?? defaultMsg,
          description: body?.description ?? null,
          details: pretty ?? body?.details ?? null,
          fields,
          raw: body,
        }
        console.error('error', error)
        const span = Sentry.startInactiveSpan({
          name: 'api-client.request.error',
          op: 'api.request',
        })
        span.addEvent('error', {
          error,
        })
        span.end()
        this.onError(error.code, error)
      }

      // 401 handling
      if (
        response.status === 401 &&
        typeof window !== 'undefined' &&
        typeof window.handle401 === 'function'
      ) {
        window.handle401()
      }
      if (
        response.status === 500 &&
        typeof window !== 'undefined' &&
        typeof window.handle500 === 'function'
      ) {
        window.handle500()
      }
    }

    return response
  }

  async requestInternal(options) {
    const headers = options.headers || {}
    const token = headers['Authorization']
      ? headers['Authorization'].replace('Bearer ', '')
      : null
    if (!token) {
      // Not actually used. useApi.withTokenArg() is used instead.
      console.error(JSON.stringify(options))
      return {
        ok: false,
        status: 401,
        body: { message: 'No access token found' },
      }
    }

    // build query string
    let query = new URLSearchParams(options.query || {}).toString()
    if (query !== '') {
      query = '?' + query
    }

    let response
    let formData
    if (options?.body instanceof File) {
      formData = new FormData()
      formData.append('file', options.body)
    }
    try {
      response = await fetch('/api' + options.url + query, {
        method: options.method,
        headers: {
          ...(formData ? {} : { 'Content-Type': 'application/json' }),
          'ngrok-skip-browser-warning': 'true',
          ...headers,
        },
        credentials: options.url === '/tokens' ? 'include' : 'omit',
        body: options.body
          ? formData
            ? formData
            : JSON.stringify(options.body)
          : null,
        signal: options.signal,
      })
    } catch (error) {
      if (error.name === 'AbortError') {
        throw error
      }
      response = {
        ok: false,
        status: 500,
        headers: {
          get: () => 'application/json',
        },
        json: async () => ({
          code: 500,
          message: 'The server is unresponsive',
          description: error.toString(),
        }),
      }
    }
    // loose JSON check
    const contentType = response.headers.get('content-type') || ''
    const isJSON = contentType.includes('application/json')

    return {
      ok: response.ok,
      status: response.status,
      body:
        response.status !== 204
          ? isJSON
            ? await response.json()
            : response
          : null,
    }
  }

  async get(url, query = {}, options = {}) {
    return this.request({ method: 'GET', url, query, ...options })
  }

  async post(url, body = {}, options = {}) {
    return this.request({ method: 'POST', url, body, ...options })
  }

  async put(url, body = {}, options = {}) {
    return this.request({ method: 'PUT', url, body, ...options })
  }

  async delete(url, body = {}, options = {}) {
    return this.request({ method: 'DELETE', url, body, ...options })
  }
  /** Base-64 to UTF-8 decoder. */
  b64 = raw => {
    if (typeof raw !== 'string') return ''
    try {
      const bin = atob(raw)
      const bytes = Uint8Array.from(bin, c => c.charCodeAt(0))
      return new TextDecoder('utf-8').decode(bytes)
    } catch {
      return raw
    }
  }

  // Streaming chat via SSE (Server-Sent Events)
  async streamAgentMessage(streamUrl, { onMessage, signal, query } = {}) {
    const traceData = Sentry.getTraceData()
    const sentryTrace = traceData['sentry-trace']
    const baggage = traceData['baggage']
    const url = new URL(streamUrl, window.location.origin)
    const params = url.searchParams
    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value === undefined || value === null) return
        params.set(key, String(value))
      })
    }
    if (sentryTrace) {
      params.append('sentry-trace', sentryTrace)
    }
    if (baggage) {
      params.append('baggage', baggage)
    }
    url.search = params.toString()
    streamUrl = url.toString()
    const es = new window.EventSource(streamUrl)
    signal?.addEventListener('abort', () => {
      onMessage?.('done', 'Aborted by user')
      es.close()
    })

    es.addEventListener('message', e => {
      if (!e.data) return

      const { type, data, is_b64 } = JSON.parse(e.data)

      // 🔑 Decode only the *string* payload events that the backend Base-64s
      const decoded = is_b64 ? this.b64(data) : data

      if (type === 'done' || type === 'error') es.close()

      onMessage?.(type, decoded)
    })

    es.addEventListener('error', e => {
      es.close()
      onMessage?.('error', e.message || 'SSE connection error')
    })
    return () => {
      es.close()
    }
  }
  async postMessage(body, options = {}) {
    return this.post('/messages', body, options)
  }

  async getAllConversations(query = {}, options = {}) {
    return this.get('/conversations', query, options)
  }
  async getConversationMessages(conversationId, query = {}, options = {}) {
    return this.get(`/conversations/${conversationId}/messages`, query, options)
  }
  async createConversationWithMessage(body, options = {}) {
    return this.post('/conversations/with-message', body, options)
  }
  async updateMessage(messageId, body, options = {}) {
    return this.put(`/messages/${messageId}`, body, options)
  }
  async updateConversation(conversationId, body, options = {}) {
    return this.put(`/conversations/${conversationId}`, body, options)
  }
  async deleteConversation(conversationId, options = {}) {
    return this.delete(`/conversations/${conversationId}`, {}, options)
  }
  async getProjects(query = {}, options = {}) {
    return this.get('/projects', query, options)
  }
  async archiveProject(projectId, options = {}) {
    return this.post(`/projects/${projectId}/archive`, {}, options)
  }
  async unarchiveProject(projectId, options = {}) {
    return this.post(`/projects/${projectId}/unarchive`, {}, options)
  }
}
